import { chartTimeSeconds, TIMEFRAME_SECONDS } from '../../../lib/chart/chartTimeProjection.js'

// One data snapshot per render/gesture, never series.data() for every pen point.
// Fractional positions interpolate between real bars; outside history they use
// timeframe spacing. These anchors do not add candles or change the time scale.
export function createDrawingProjection(api, timeframe) {
  const scale = api.chart.timeScale(), bars = api.series.data?.() || []
  const times = bars.map(b => chartTimeSeconds(b.time))
  const step = TIMEFRAME_SECONDS[timeframe] || 86400
  const indices = new Map()
  const logicalAt = i => { if (!indices.has(i)) indices.set(i, scale.timeToIndex?.(bars[i].time) ?? i); return indices.get(i) }
  const first = bars.length ? logicalAt(0) : 0
  const last = bars.length ? logicalAt(bars.length - 1) : 0
  // LWC 5.2 coordinateToLogical snaps to an integer, and logicalToCoordinate
  // rejects fractional indices. Derive continuous coordinates from two integer
  // centers instead; this also works beyond history without adding whitespace.
  const origin = scale.logicalToCoordinate?.(first), neighbor = scale.logicalToCoordinate?.(first + 1)
  const spacing = neighbor - origin
  const validScale = Number.isFinite(origin) && Number.isFinite(neighbor) && spacing > 0
  const indexAt = time => {
    if (!times.length) return null
    if (time < times[0]) return first + (time - times[0]) / step
    if (time > times.at(-1)) return last + (time - times.at(-1)) / step
    let lo = 0, hi = times.length - 1
    while (lo < hi) { const mid = Math.ceil((lo + hi) / 2); if (times[mid] <= time) lo = mid; else hi = mid - 1 }
    return logicalAt(lo) + (lo < times.length - 1 ? (time - times[lo]) / (times[lo + 1] - times[lo]) * (logicalAt(lo + 1) - logicalAt(lo)) : 0)
  }
  return {
    clockKey: () => times.map((time,i) => `${time}:${logicalAt(i)}`).join('|'),
    point({ x, y }) {
      const logical = validScale ? first + (x - origin) / spacing : null, price = api.series.coordinateToPrice(y)
      if (!times.length || !Number.isFinite(logical) || !Number.isFinite(price)) return null
      let time
      if (logical < first) time = times[0] + (logical - first) * step
      else if (logical >= last) time = times.at(-1) + (logical - last) * step
      else {
        let lo=0,hi=times.length-1
        while(lo<hi){const mid=Math.ceil((lo+hi)/2);if(logicalAt(mid)<=logical)lo=mid;else hi=mid-1}
        time=times[lo]+(logical-logicalAt(lo))/(logicalAt(lo+1)-logicalAt(lo))*(times[lo+1]-times[lo])
      }
      return { time, price }
    },
    project({ time, price }) {
      const index = indexAt(time), x = index == null || !validScale ? null : origin + (index - first) * spacing, y = api.series.priceToCoordinate(price)
      return x == null || y == null ? null : { x, y }
    },
  }
}
export function segmentDistance(p, a, b) {
  const dx = b.x - a.x, dy = b.y - a.y
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / (dx * dx + dy * dy || 1)))
  return Math.hypot(p.x - a.x - t * dx, p.y - a.y - t * dy)
}
export function strokeTouchesBox(points, a, b) {
  const left = Math.min(a.x,b.x), right = Math.max(a.x,b.x), top = Math.min(a.y,b.y), bottom = Math.max(a.y,b.y)
  for (let i=0;i<points.length;i++) {
    const p=points[i], q=points[i+1] || p
    if (!p || !q) continue
    let lo=0,hi=1
    for (const [start,delta,min,max] of [[p.x,q.x-p.x,left,right],[p.y,q.y-p.y,top,bottom]]) {
      if (delta===0) { if(start<min||start>max) {hi=-1;break} }
      else {const u=(min-start)/delta,v=(max-start)/delta;lo=Math.max(lo,Math.min(u,v));hi=Math.min(hi,Math.max(u,v))}
    }
    if(lo<=hi)return true
  }
  return false
}
